/**
 * `go:` / Pages (PLAN §2.2, S4): every page of the app with its scope prefix —
 * the owner's "all our pages should show as `something:`". Enter opens the
 * page; Tab enters its scope. A page whose provider is not registered (or not
 * available) is listed without a prefix: Enter still opens it.
 */
import {
  Send, Clock, GitBranch, MessageSquare, Calendar, Kanban, SquareCheck, FileText, Lock, Network, Database,
  Terminal, Server, Mail, FolderKanban, Users, Compass,
} from 'lucide-react';
import { ORDER } from './order';
import { selectTop } from '../localSearch';
import { openAction, enterScopeAction } from '../actions';
import { getProvider, getProviders } from '../registry';
import { useExtensionStore, isApiClientEnabled } from '../../extensions/store';
import { setActiveTab } from '../../store/uiModalStore';
import { DESKTOP_ONLY_PANELS, isWebApp } from '../../platform/target';

/** Every page: panel id, i18n key, icon, the scope its prefix enters (if any). */
export const PAGES = [
  { key: 'apiClient', panel: 'collections', icon: Send, scope: 'requests', apiClient: true },
  { key: 'history', panel: 'history', icon: Clock, apiClient: true },
  { key: 'git', panel: 'git', icon: GitBranch },
  { key: 'chat', panel: 'chat.panel', icon: MessageSquare, scope: 'chats', extension: 'chat' },
  { key: 'boards', panel: 'task-manager.panel', icon: Kanban, scope: 'boards', extension: 'task-manager' },
  { key: 'tasks', panel: 'task-manager.panel', icon: SquareCheck, scope: 'tasks', extension: 'task-manager', id: 'tasks-page' },
  { key: 'documents', panel: 'documents.panel', icon: FileText, scope: 'documents', extension: 'documents' },
  { key: 'calendar', panel: 'calendar.panel', icon: Calendar, scope: 'calendar', extension: 'calendar' },
  { key: 'projects', panel: 'projects.panel', icon: FolderKanban, scope: 'projects', extension: 'projects' },
  { key: 'vault', panel: 'vault.panel', icon: Lock, extension: 'vault' },
  { key: 'graph', panel: 'graph.panel', icon: Network, extension: 'graph' },
  { key: 'dbManager', panel: 'db-manager.panel', icon: Database, extension: 'db-manager' },
  { key: 'terminal', panel: 'terminal.panel', icon: Terminal, extension: 'terminal' },
  { key: 'ftp', panel: 'ftp.panel', icon: Server, extension: 'ftp' },
  { key: 'mailCatcher', panel: 'mail-catcher.panel', icon: Mail, extension: 'mail-catcher' },
  { key: 'teams', panel: 'teams', icon: Users, signedIn: true },
];

/** Open a page the way the rail does: keep the extension panel alive, then switch. */
export function openPage(panel) {
  if (panel.endsWith('.panel')) useExtensionStore.getState().activatePanel?.(panel);
  setActiveTab(panel);
}

/** The provider a page's prefix enters, when it is registered and available now. */
export function scopeProviderFor(page, ctx) {
  if (!page.scope) return null;
  const p = getProvider(page.scope);
  if (!p) return null;
  try {
    return p.available(ctx) ? p : null;
  } catch {
    return null;
  }
}

function pageAvailable(page, ctx) {
  if (page.signedIn && !ctx.currentUserId) return false;
  if (page.apiClient && !isApiClientEnabled()) return false;
  if (isWebApp() && DESKTOP_ONLY_PANELS.has(page.panel)) return false;
  if (page.panel.endsWith('.panel')) {
    // The extension that contributes this panel must be enabled.
    const { extensions = {}, enabled = {} } = useExtensionStore.getState();
    const owner = Object.keys(extensions).find((id) =>
      (extensions[id]?.contributes?.panels || extensions[id]?.panels || []).some((p) => p?.id === page.panel)
    );
    if (owner && enabled[owner] === false) return false;
  }
  return true;
}

export function pageResult(page, ctx) {
  const scopeProvider = scopeProviderFor(page, ctx);
  const prefix = scopeProvider ? `${scopeProvider.prefixes[0]}:` : null;
  return {
    provider: 'pages',
    id: page.id || page.panel,
    title: ctx.t(`palette.page.${page.key}`),
    icon: page.icon,
    searchFields: prefix ? [{ text: prefix, weight: 0.4 }] : [],
    payload: { panel: page.panel, scopeProviderId: scopeProvider ? scopeProvider.id : null, prefix },
  };
}

let pagesCache = { key: null, rows: [] };

/** Every page as a row; rebuilt when the language, sign-in, extensions or providers change. */
export function listPages(ctx) {
  const { enabled } = useExtensionStore.getState();
  const providers = getProviders();
  const key = `${ctx.currentUserId ? 1 : 0}|${ctx.platform}|${providers.length}|${isApiClientEnabled() ? 1 : 0}`;
  if (pagesCache.key === key && pagesCache.t === ctx.t && pagesCache.enabled === enabled && pagesCache.providers === providers.map((p) => p.id).join(',')) {
    return pagesCache.rows;
  }
  const rows = PAGES.filter((p) => pageAvailable(p, ctx)).map((p) => pageResult(p, ctx));
  pagesCache = { key, t: ctx.t, enabled, providers: providers.map((p) => p.id).join(','), rows };
  return rows;
}

export default function create() {
  return {
    id: 'pages',
    prefixes: ['go', 'g', 'pages'],
    labelKey: 'palette.scope.pages',
    icon: Compass,
    order: ORDER.pages,
    kind: 'local-sync',
    available: () => true,
    search(req) {
      return selectTop(listPages(req.ctx), req);
    },
    async recent(ids, ctx) {
      const all = listPages(ctx);
      return ids.map((id) => all.find((r) => r.id === id)).filter(Boolean);
    },
    actions(result) {
      const out = [openAction((r) => openPage(r.payload.panel))];
      if (result.payload?.scopeProviderId) out.push(enterScopeAction(result.payload.scopeProviderId));
      return out;
    },
  };
}
